<template>
  <div class="pb-96">
    <Popup :value="showAutoPopulatePopup" popupHeader="Add your Back-Up Care info?">
      <p>Do you want to add your Back-Up care information into your job post or create your own?</p>
      <Button value="Add my info" class="w-full mt-16" size="medium" @click="populateJobPostWithCareSessionData" />
      <Button
        value="Create my own job"
        class="w-full mt-16"
        size="medium"
        variant="outlined"
        @click="showAutoPopulatePopup = false"
      />
    </Popup>
    <RouteStepper v-model="jobPostDraft" contentClass="md:mt-24 md:w-568 px-16 mx-auto" />
  </div>
</template>

<script lang="ts">
import RouteStepper from '@/components/route_stepper/RouteStepper.vue';
import { PREPOPULATE_RESERVATION_QUERY } from '@/graphql/backup_care/queries';
import { extractJobDataFromCareSessions } from './helpers/backupCareJobPostHelpers';
import Popup from '@/components/popup/Popup.vue';
import Button from '@/components/button/Button.vue';
import { JobPostDraft } from './types';

interface JobPostData {
  jobPostDraft: JobPostDraft;
  showAutoPopulatePopup: boolean;
  reservation: { scCareSessions: any[] } | null;
}

export default {
  name: 'JobPost',
  components: { RouteStepper, Popup, Button },
  data(): JobPostData {
    return {
      jobPostDraft: {
        title: '',
        description: '',
        ageGroups: [],
        requirements: [],
        responsibilities: [],
        languages: [],
        location: null,
        numberOfChildren: null,
        paymentRate: null,
        schedule: null,
        aiGenerated: false,
        zipCode: '',
        isPrepopulatedFromReservation: false,
      },
      showAutoPopulatePopup: false,
      reservation: null,
    };
  },
  mounted() {
    if (this.reservationIdQueryParam) {
      // fetch reservation data from the query parameter
      this.setReservationFromQueryParam(this.reservationIdQueryParam);
    } else if (this.$store.getters['account/getAccount'].isCorporate()) {
      // fetch the most recent active reservation
      this.setReservationFromMostRecentActive();
    }
  },
  watch: {
    reservation(): void {
      if (this.reservation !== null) {
        // if reservation data is available, show the auto-populate popup
        this.showAutoPopulatePopup = true;
      }
    },
  },
  computed: {
    isCorporateUser(): boolean {
      return this.$store.getters['account/getAccount'].roles.includes('"com.sittercity.empire:Role.corporate_seeker"');
    },
    reservationIdQueryParam(): string | null {
      const reservationId = this.$route?.query?.reservationId;
      if (Array.isArray(reservationId) || !reservationId) {
        return null;
      }

      return reservationId;
    },
  },
  methods: {
    async getReservationsQuery(): Promise<any[]> {
      try {
        const { data } = await this.$apollo.query({
          query: PREPOPULATE_RESERVATION_QUERY,
        });
        return data?.corp?.backupCare?.bhReservationRequests || [];
      } catch (error) {
        console.error(error); // eslint-disable-line no-console
        return [];
      }
    },
    async setReservationFromQueryParam(reservationId: string): Promise<void> {
      // Fetch reservation data from the query parameter
      // and set it to the reservation data
      const reservations = await this.getReservationsQuery();
      const reservationFromId = reservations.find((reservation: any) => reservation?.bhReservationRequestId === reservationId);
      if (!reservationFromId) {
        console.warn('No reservation found with the given ID');
        return;
      }

      this.reservation = reservationFromId;
    },
    async setReservationFromMostRecentActive(): Promise<void> {
      // Fetch the most recent active reservation
      // and set it to the reservation data
      const reservations = await this.getReservationsQuery();
      if (!reservations || reservations.length === 0) {
        console.warn('No reservations found');
        return;
      }
      const currentDateISO: string = new Date().toISOString();
      const mostRecentActiveReservation = reservations.reduce((mostRecentReservation: any, reservation: any) => {
        const startDateExpired: boolean = reservation.expired < currentDateISO;
        const isMoreRecentSession: boolean =
          reservation.createdAt > mostRecentReservation?.createdAt || mostRecentReservation === null;
        // check if the session is valid start time and more recent
        if (!startDateExpired && isMoreRecentSession) {
          return reservation;
        }
        return mostRecentReservation;
      }, null);

      this.reservation = mostRecentActiveReservation;
    },
    populateJobPostWithCareSessionData(): void {
      const careSessionData = this.reservation?.scCareSessions || [];

      if (careSessionData.length === 0) {
        console.warn('No reservation data available to populate the job post');
        this.showAutoPopulatePopup = false;
        return;
      }

      const extractedJobData: JobPostDraft = extractJobDataFromCareSessions(careSessionData);
      const { scheduleType, ageGroups, location, zipCode, schedule, numberOfChildren } = extractedJobData;

      this.jobPostDraft = {
        ...this.jobPostDraft,
        scheduleType,
        ageGroups,
        location,
        zipCode,
        schedule,
        numberOfChildren,
        isPrepopulatedFromReservation: true,
      };

      this.showAutoPopulatePopup = false;
    },
  },
};
</script>
